import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { IActionRegister, IHistoryItem, IRegisterReducer } from 'core/slices/register';
import { IEntityReducer } from '../../../reducers/entities';
import { selectHistory } from '../history';
import { IStateReducer } from 'core/reducers/state';
import { ActionStatus, ActionType } from 'types/domain/action';
import { INodeProps, selectModuleNodesAndEdges } from 'core/selectors/admin/flow';
import { reduceArrayById } from 'utils/reduceArrayById';

const entity = (state: RootState) => state.entity.present;
const register = (state: RootState) => state.register;
const state = (state: RootState) => state.state;
const history = selectHistory;
const selectedNode = (state: RootState) => state.flow?.selectedNode;
const mode = (state: RootState) => state.session.mode;

export type ActionHistory = Record<number, ActionHistoryItem[]>;
export type ActionHistoryItem = {
  actionId: number;
  actionRegisterId: string;
};

export const selectActionHistory = createSelector([entity, history, register, state], parseActionHistory);

export function parseActionHistory(
  entity: IEntityReducer,
  history: IHistoryItem[],
  register: IRegisterReducer,
  state: IStateReducer,
) {
  return history
    .filter(
      hist => hist.courseProgressId === state[EntityTypes.COURSE_PROGRESS] && register.attempts[hist.sessionAttemptId],
    )
    .reduce((prev, hist: IHistoryItem) => {
      prev[hist.sessionAttemptId] = Object.values(register.attempts[hist.sessionAttemptId].actions)
        .filter(
          a =>
            !!entity[EntityTypes.ACTION][a.actionId] &&
            entity[EntityTypes.ACTION][a.actionId].status !== ActionStatus.DELETED,
        )
        .sort((a, b) => (a.created < b.created ? -1 : 1))
        .map((ar: IActionRegister) => {
          return {
            actionId: ar.actionId,
            actionRegisterId: ar.actionRegisterId,
          };
        });
      return prev;
    }, {} as ActionHistory);
}

export const selectSimulatedActionHistory = createSelector(
  [state, selectModuleNodesAndEdges, selectedNode, mode],
  (state, moduleNodesAndEdges, selectedNode, mode) => {
    if (!moduleNodesAndEdges) return [];

    const actionTree = [];
    const actions = moduleNodesAndEdges.nodes
      .filter(n => ![ActionType.FUTURE].includes(n.data.type as ActionType))
      .map(n => n.data);
    const actionsById = reduceArrayById(actions);
    const id = state[EntityTypes.ACTION];

    buildEditHistory(id, actions, actionTree);

    let history = actionTree.reverse();

    return history.map(actionId => ({ actionId, actionType: actionsById[actionId]?.type, actionRegisterId: null }));
  },
);

const buildEditHistory = (actionId: number, nodes: INodeProps[], history: number[]) => {
  history.push(actionId);
  const index = nodes.findIndex(n => n.id === actionId);

  if (nodes[index]?.parentActionId) buildEditHistory(nodes[index]?.parentActionId, nodes, history);
  else if (index > 0) buildEditHistory(nodes[index - 1]?.id, nodes, history);
};
